import { Injectable } from '@angular/core';
import { get } from 'lodash';
import { datadogRum, RumInitConfiguration } from '@datadog/browser-rum';
import { IUser } from '../interfaces/user';
import { EnvironmentService } from './environment.service';
import { LoggerService } from './logger.service';
import { TaggedLogger } from './taggedLogger';
import { environment } from '../../environments/environment';
import { UserService } from './user.service';
import { SystemDataService } from './system-data.service';
import { datadogLogs } from '@datadog/browser-logs';
/**
 * Datadog RUM Service
 *
 * Initializes Datadog RUM tracing
 */
@Injectable({ providedIn: 'root' })
export class DatadogRumService {
  private readonly defaultEnvironment: string;

  private readonly logger: TaggedLogger;

  environment: string;

  applicationId: string;

  clientToken: string;

  site: string;

  service: string;

  sampleRate: string;

  tagSessionsWithUser: boolean;

  /**
   * @constructor
   */
  constructor(
    private systemService: SystemDataService,
    private environmentService: EnvironmentService,
    private userService: UserService,
    loggerService: LoggerService,
  ) {
    this.logger = loggerService.taggedLogger('service:datadog-rum');
    // this.version = (pkg as any).version;

    this.defaultEnvironment = get(environment, 'datadog.rum.environment', '');
    this.applicationId = get(environment, 'datadog.rum.applicationId', '');
    this.clientToken = get(environment, 'datadog.rum.clientToken', '');
    this.site = get(environment, 'datadog.rum.site', '');
    this.service = get(environment, 'datadog.rum.service', '');
    this.sampleRate = get(environment, 'datadog.rum.sampleRate', 100);
    this.tagSessionsWithUser = get(environment, 'datadog.rum.tagSessionsWithUser', false);

    this.environment = this.getEnvironment();
  }

  /**
   * Initializes the service
   */
  async init(logsOnly: boolean = false) {
    const { pipeline } = await this.systemService.getVersionInfo();

    datadogLogs.init({
      clientToken: this.clientToken,
      site: this.site,
      service: this.service,
      env: this.environment,
      version: pipeline,
    });

    if (logsOnly) {
      return;
    }

    if (!this.isConfigValid()) return;

    const config: RumInitConfiguration = {
      applicationId: this.applicationId,
      clientToken: this.clientToken,
      site: this.site,
      service: this.service,
      env: this.environment,
      version: pipeline,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      allowedTracingUrls: [this.getTracingUrl()],
      sessionReplaySampleRate: 100,
      sessionSampleRate: 100,
      allowFallbackToLocalStorage: true,
      defaultPrivacyLevel: 'allow',
      trackingConsent: 'granted',
    };

    datadogRum.init(config);

    this.setUserAndListen();

    this.userService.logout$.subscribe(() => this.endSession());
  }

  private getTracingUrl(): string {
    return `${window.location.origin}/api`;
  }

  public addAction(name: string, context: any) {
    datadogRum.addAction(name, context);
  }

  /**
   * Is the configuration for datadog valid?
   * @private
   * @return true if the configuration is valid, false if the configuration is missing values
   */
  private isConfigValid() {
    if (!this.environment) {
      this.logger.warn('Datadog environment not configured, not initializing RUM.');
      return false;
    }

    if (!this.applicationId) {
      this.logger.warn('Datadog applicationId not configured, not initializing RUM.');
      return false;
    }

    if (!this.clientToken) {
      this.logger.warn('Datadog clientToken not configured, not initializing RUM.');
      return false;
    }

    if (!this.site) {
      this.logger.warn('Datadog site not configured, not initializing RUM.');
      return false;
    }

    if (!this.service) {
      this.logger.warn('Datadog service not configured, not initializing RUM.');
      return false;
    }

    return true;
  }

  /**
   * Gets the environment tag we should send to datadog
   * Either from the environment service's host check, or the environment.ts config file.
   *
   * @return the environment tag to use
   * @private
   */
  private getEnvironment() {
    if (this.environmentService.isUnknown()) {
      return this.defaultEnvironment;
    }

    return this.environmentService.getName();
  }

  /**
   * Initially sets the user then subscribes to any changes.
   * @private
   */
  private setUserAndListen() {
    if (!this.tagSessionsWithUser) return;

    const currentUser = this.userService.currentUser;

    this.setUser(currentUser);

    this.userService.user$.subscribe((user: IUser) => this.setUser(user));
  }

  /**
   * Updates the user
   * @param currentUser The current user or null if unset
   * @private
   */
  private setUser(currentUser: IUser) {
    datadogLogs.logger.info('Attempting to set datadog user');

    if (!currentUser) {
      datadogLogs.logger.error('User was null when attempting to set datadog user');
      return;
    }

    let ddUser: { id: string; email: string; name: string };

    if (currentUser.impersonationInfo.isImpersonating) {
      ddUser = {
        id: currentUser.impersonationInfo.originalUserId,
        email: currentUser.impersonationInfo.originalUserEmail,
        name: currentUser.impersonationInfo.originalUserName,
      };
    } else {
      ddUser = {
        id: currentUser.id,
        email: currentUser.email,
        name: `${currentUser.firstName} ${currentUser.lastName}`,
      };
    }

    datadogLogs.logger.info(`Setting datadog user to ${ddUser.email}`, ddUser);

    datadogRum.setUser(ddUser);
  }

  public endSession() {
    //datadogRum.stopSessionReplayRecording();
    datadogRum.stopSession();
  }
}
